/* script */
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { cloneDeep } from 'lodash';
import DropdownMenu from 'shared/ui/components/DropdownMenu.vue';
import PreviewAsUser from './PreviewAsUser.vue';
import { compileWithObject } from 'shared/libs/utils/string';
import { isFieldProtected } from 'shared/libs/utils/metadata';

import { BadgeEditorController } from 'shared/libs/providers/badge-editor/controller';
import { MultipageOptions } from '../utils';
import { unitToPixels } from 'shared/libs/utils/numbers';
import { BLANK_STATE } from 'shared/libs/providers/badge-editor/state';
import { PRE_PRINT_CAPABLE_BADGE_PRINTING_PRESETS_SET } from 'shared/libs/utils/constants';

const __vue_script__ = {
    name: 'PreviewOptions',

    components: { DropdownMenu, PreviewAsUser },
    props: {
        event: {
            type: Object,
            required: true,
        },
        badgeModel: {
            type: Object,
            required: true,
        },
        imageCache: {
            type: Map,
            required: true,
        },
        multiPage: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            controller: null,
            previewModel: [],
        };
    },

    computed: {
        hasPrePrintAvailable() {
            return PRE_PRINT_CAPABLE_BADGE_PRINTING_PRESETS_SET.has(this.badgeModel.preset);
        },
        hasFinalAvailable() {
            // same check, other printers can print in color and we swap their preprinted with final
            // original preprinted is available on black&white printers (pre_print_capable).
            return PRE_PRINT_CAPABLE_BADGE_PRINTING_PRESETS_SET.has(this.badgeModel.preset);
        },
    },

    async mounted() {
        this.controller = new BadgeEditorController(this.$refs.canvas, this.$refs.wrapper, {}, this.imageCache);
        this.controller.updateBackdropDimensions(
            unitToPixels(this.badgeModel.width, this.badgeModel.dpi, this.badgeModel.unit),
            unitToPixels(this.badgeModel.height, this.badgeModel.dpi, this.badgeModel.unit),
        );
    },

    methods: {
        /**
         * Prints without dynamic elements - no qr code, remove text substitution tags
         */
        prePrinted() {
            const ctx = this.prepareEmptyContext();
            const previewModel = this.preparePreview(ctx);

            for (const page of previewModel) {
                const pageBlocks = page.blocks;
                pageBlocks.qrcode = [];
            }

            this.loadAsPdf(previewModel);
        },
        printedOnSiteOrFinal() {
            if (this.hasFinalAvailable) {
                return this.printedOnSite();
            }

            this.finalBadge();
        },
        /**
         * Print as is visible in the editor - minus background, no text substitution is done.
         */
        printedOnSite() {
            const previewModel = this.preparePreview({});

            for (const page of previewModel) {
                const pageBlocks = page.blocks;
                pageBlocks.background = {};
            }

            this.loadAsPdf(previewModel);
        },
        /**
         * Print as is visible in the editor - no text substitution is done.
         */
        finalBadge() {
            const previewModel = this.preparePreview({});

            this.loadAsPdf(previewModel);
        },
        /**
         * Prints as is visible in the editor - but substitute tags with user data.
         */
        previewAsUser() {
            this.$modal.show(PreviewAsUser, {
                event: this.event,
                onPreview: async (pax) => {
                    const ctx = await this.prepareUserContext(pax._id);
                    const previewModel = this.preparePreview(ctx);

                    this.$modal.hide('previewAsUserModal');

                    this.loadAsPdf(previewModel);
                },
            }, {
                name: 'previewAsUserModal',
                classes: 'small',
                height: 'auto',
                clickToClose: false
            });
        },
        /**
         * Returns a record with pax data for each used variable
         * @type {(paxId: string) => Promise<Record<string,unknown>>}
         */
        async prepareUserContext(paxId) {
            const paxDetails = await this.$services.pax.getPax(this.event._id, paxId);
            const ctx = {};
            // add values from pax when it exists and is not a protected field
            // otherwise set as empty string
            for (const key of this.getAllVariables()) {
                let value = paxDetails[key];
                // ensure 0 and boolean false are truthy
                if (value === 0 || value === false) value = String(value);
                ctx[key] = (isFieldProtected(key) || !value) ? '' : value;
            }
            // ensure private key is "placeholder"
            ctx.private_key = 'placeholder';
            return ctx;
        },
        /**
         * Gets all variables used in texts and qr codes
         * @returns {string[]}
         */
        getAllVariables() {
            /** @type {(str: string)} */
            const extractVariables = (str) => {
                const matches = str.match(/\{\{?(\w+)\}\}?/g) || [];
                return matches.map(match => match.replaceAll(/\{+|\}+/g, ''));
            };

            const vars = new Set();

            for (const page of this.badgeModel.pages) {
                const pageBlocks = page.blocks;

                for (const t of pageBlocks.text) {
                    for (const v of extractVariables(t.text)) {
                        vars.add(v);
                    }
                }

                for (const qr of pageBlocks.qrcode) {
                    for (const v of extractVariables(qr.content)) {
                        vars.add(v);
                    }
                }
            }

            return Array.from(vars);
        },
        /**
         * Match and replace all {{keys}} to dict { key: '', ... } with empty strings as values.
         * @returns {Record<string,string>}
         */
        prepareEmptyContext() {
            let ctx = {};
            for (const key of this.getAllVariables()) ctx[key] = '';
            return {
                ...ctx,
                private_key: 'placeholder',
            };
        },
        preparePreview(ctx) {
            const previewModel = cloneDeep(this.badgeModel.pages);
            for (const page of previewModel) {
                const pageBlocks = page.blocks;

                for (const t of pageBlocks.text) {
                    t.text = compileWithObject(t.text, ctx);
                    t.useDynamicFontSize = true;
                }

                for (const qr of pageBlocks.qrcode) {
                    qr.content = compileWithObject(qr.content, ctx);
                }
            }

            return previewModel;
        },
        async loadAsPdf(previewModel) {
            const pagesToRender = [];
            switch (this.multiPage) {
                case MultipageOptions.SINGLE:
                    pagesToRender.push(previewModel[0]);
                    break;
                case MultipageOptions.DOUBLE:
                    pagesToRender.push(previewModel[0], previewModel[1]);
                    break;
                case MultipageOptions.MIRRORED:
                    pagesToRender.push(previewModel[0], previewModel[0]);
                    break;
            }

            const size = this.controller.getBackdropDimensions();

            const renderedSvg = [];
            for (const page of pagesToRender) {
                this.controller.replaceState(page);

                await new Promise(resolve => {
                    // without waiting we'll export canvas as is, before textbox has time to resize font.
                    requestIdleCallback(() => {
                        renderedSvg.push(this.controller.asSVG());

                        // reset state; avoids issue with textbox not resizing correctly
                        this.controller.replaceState(BLANK_STATE);

                        resolve('');
                    });
                });
            }

            await this.openAsPDF(renderedSvg, size);
        },
        async openAsPDF(pagesData, { width, height }) {
            /** @type {{ data: number[], type: 'Buffer' | string}} */
            const { data, type } = await this.$services.appScripts.createJobAndWaitForCompletion(this.event._id, 'pdf/export-svg-to-pdf', { data: pagesData, size: { width, height } });

            if (type.toLocaleLowerCase() === 'buffer') {
                const blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });
                window.open(URL.createObjectURL(blob), '_blank');
            }
        },
    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"actions-dropdown"},[_c('dropdown-menu',{staticClass:"actions",attrs:{"anchor-pos":"sw"},scopedSlots:_vm._u([{key:"toggler",fn:function(){return [_c('button',{staticClass:"btn btn-icon btn-tertiary select-action"},[_c('span',[_vm._v(_vm._s(_vm.$t('onsite_scanning.actions.preview_download')))]),_c('i',{staticClass:"icon ic-chevron-up"})])]},proxy:true}])},[_c('ul',[(_vm.hasPrePrintAvailable)?_c('li',[_c('a',{on:{"click":_vm.prePrinted}},[_vm._v(_vm._s(_vm.$t('onsite_scanning.actions.pre_printed')))])]):_vm._e(),_c('li',[_c('a',{on:{"click":_vm.printedOnSiteOrFinal}},[_vm._v(_vm._s(_vm.$t('onsite_scanning.actions.printed_onsite')))])]),(_vm.hasFinalAvailable)?_c('li',[_c('a',{on:{"click":_vm.finalBadge}},[_vm._v(_vm._s(_vm.$t('onsite_scanning.actions.final')))])]):_vm._e(),_c('li',[_c('a',{on:{"click":_vm.previewAsUser}},[_vm._v(_vm._s(_vm.$t('onsite_scanning.actions.preview_as_user')))])])])]),_c('div',{ref:"wrapper",staticClass:"wrapper",staticStyle:{"display":"none"}},[_c('canvas',{ref:"canvas"})])],1)}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "PreviewOptions.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__